import React, { useEffect, useRef, useState } from 'react'
import { LoadingSkeleton } from '../LoadingSkeleton'
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import PropTypes from 'prop-types'
import { deleteImage, updateImage, uploadImage } from '../backend'
import { fetchImages } from '../backend'

export default function AdminImageManagement() {
  const [images, setImages] = useState([])
  const [loading, setLoading] = useState(false)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [editingImage, setEditingImage] = useState(null)
  const [deletingImage, setDeletingImage] = useState(null)
  const [snackMessage, setSnackMessage] = useState({ message: '', severity: 'success' })
  const [uploadForm, setUploadForm] = useState({ file: null, title: '', description: '', doubleImage: false })
  const fileInputRef = useRef(null)

  useEffect(function () {
    loadImages()
  }, [])

  function loadImages() {
    setLoading(true)
    fetchImages()
      .then(function (data) {
        setImages(data)
        setLoading(false)
      })
      .catch(function () {
        setSnackMessage({ message: 'Erreur lors du chargement des images', severity: 'error' })
        setLoading(false)
      })
  }

  function handleUploadOpen() {
    setUploadForm({ file: null, title: '', description: '', doubleImage: false })
    setUploadOpen(true)
  }

  function handleUploadClose() {
    setUploadOpen(false)
  }

  function handleUploadSubmit() {
    if (!uploadForm.file) {
      setSnackMessage({ message: 'Veuillez sélectionner un fichier', severity: 'error' })
      return
    }
    setLoading(true)
    uploadImage(uploadForm.file, uploadForm.title, uploadForm.description, uploadForm.doubleImage)
      .then(function () {
        setUploadOpen(false)
        loadImages()
        setSnackMessage({ message: 'Image ajoutée', severity: 'success' })
        setLoading(false)
      })
      .catch(function () {
        setSnackMessage({ message: "Erreur lors de l'upload", severity: 'error' })
        setLoading(false)
      })
  }

  function handleEditOpen(image) {
    setEditingImage({
      id: image.id,
      title: image.title,
      description: image.description,
      doubleImage: image.rows === 2
    })
    setEditOpen(true)
  }

  function handleEditClose() {
    setEditOpen(false)
    setEditingImage(null)
  }

  function handleEditSubmit() {
    if (!editingImage) return
    setLoading(true)
    updateImage(editingImage.id, editingImage.title, editingImage.description, editingImage.doubleImage)
      .then(function () {
        setEditOpen(false)
        setEditingImage(null)
        loadImages()
        setSnackMessage({ message: 'Image modifiée', severity: 'success' })
        setLoading(false)
      })
      .catch(function () {
        setSnackMessage({ message: 'Erreur lors de la modification', severity: 'error' })
        setLoading(false)
      })
  }

  function handleDeleteOpen(image) {
    setDeletingImage(image)
    setDeleteOpen(true)
  }

  function handleDeleteClose() {
    setDeleteOpen(false)
    setDeletingImage(null)
  }

  function handleDeleteSubmit() {
    if (!deletingImage) return
    setLoading(true)
    deleteImage(deletingImage.id)
      .then(function () {
        setDeleteOpen(false)
        setDeletingImage(null)
        loadImages()
        setSnackMessage({ message: 'Image supprimée', severity: 'success' })
        setLoading(false)
      })
      .catch(function () {
        setSnackMessage({ message: 'Erreur lors de la suppression', severity: 'error' })
        setLoading(false)
      })
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant='h6' fontWeight='bold' color='primary'>
          Gestion des images
        </Typography>
        <Button variant='outlined' startIcon={<AddIcon />} onClick={handleUploadOpen} size='small'>
          Ajouter une image
        </Button>
      </Box>

      {loading ? (
        <LoadingSkeleton text={'Chargement des images ...'} />
      ) : images.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant='body2' color='text.secondary' sx={{ fontStyle: 'italic' }}>
            Aucune image uploadée
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {images.map(function (image) {
            return <ImageCard key={image.id} image={image} onEdit={handleEditOpen} onDelete={handleDeleteOpen} />
          })}
        </Stack>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onClose={handleUploadClose} maxWidth='sm' fullWidth>
        <DialogTitle>Ajouter une image</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2}>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main' }
              }}
              onClick={function () {
                fileInputRef.current && fileInputRef.current.click()
              }}
            >
              <input
                type='file'
                accept='image/*'
                ref={fileInputRef}
                onChange={function (e) {
                  var files = e.target.files
                  if (files && files[0]) {
                    setUploadForm(function (prev) {
                      return Object.assign({}, prev, { file: files[0] })
                    })
                  }
                }}
                style={{ display: 'none' }}
              />
              <Typography variant='body2' color='text.secondary'>
                {uploadForm.file ? uploadForm.file.name : 'Cliquez ou glissez un fichier ici'}
              </Typography>
            </Box>
            <TextField
              label='Titre'
              value={uploadForm.title}
              onChange={function (e) {
                setUploadForm(function (prev) {
                  return Object.assign({}, prev, { title: e.target.value })
                })
              }}
              fullWidth
            />
            <TextField
              label='Description'
              value={uploadForm.description}
              onChange={function (e) {
                setUploadForm(function (prev) {
                  return Object.assign({}, prev, { description: e.target.value })
                })
              }}
              fullWidth
              multiline
              rows={3}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={uploadForm.doubleImage}
                  onChange={function (e) {
                    setUploadForm(function (prev) {
                      return Object.assign({}, prev, { doubleImage: e.target.checked })
                    })
                  }}
                />
              }
              label='Image Double (2x2)'
            />
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={handleUploadClose}>Annuler</Button>
          <Button variant='contained' onClick={handleUploadSubmit} disabled={!uploadForm.file}>
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={handleEditClose} maxWidth='sm' fullWidth>
        <DialogTitle>Modifier l&apos;image</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2}>
            <TextField
              label='Titre'
              value={editingImage ? editingImage.title : ''}
              onChange={function (e) {
                if (editingImage) {
                  setEditingImage(Object.assign({}, editingImage, { title: e.target.value }))
                }
              }}
              fullWidth
            />
            <TextField
              label='Description'
              value={editingImage ? editingImage.description : ''}
              onChange={function (e) {
                if (editingImage) {
                  setEditingImage(Object.assign({}, editingImage, { description: e.target.value }))
                }
              }}
              fullWidth
              multiline
              rows={3}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editingImage ? editingImage.doubleImage : false}
                  onChange={function (e) {
                    if (editingImage) {
                      setEditingImage(Object.assign({}, editingImage, { doubleImage: e.target.checked }))
                    }
                  }}
                />
              }
              label='Image Double (2x2)'
            />
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={handleEditClose}>Annuler</Button>
          <Button variant='contained' onClick={handleEditSubmit}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteOpen} onClose={handleDeleteClose} maxWidth='sm' fullWidth>
        <DialogTitle>Supprimer l&apos;image</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant='body1'>Êtes-vous sûr de vouloir supprimer cette image ?</Typography>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={handleDeleteClose}>Annuler</Button>
          <Button variant='contained' color='error' onClick={handleDeleteSubmit}>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackMessage.message}
        autoHideDuration={4000}
        onClose={function () {
          setSnackMessage({ message: '', severity: 'success' })
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={function () {
            setSnackMessage({ message: '', severity: 'success' })
          }}
          severity={snackMessage.severity}
          variant='filled'
          sx={{ width: '100%' }}
        >
          {snackMessage.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

function ImageCard({ image, onEdit, onDelete }) {
  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: 1,
          overflow: 'hidden',
          flexShrink: 0,
          backgroundColor: 'grey.100'
        }}
      >
        {image.img ? (
          <img
            src={image.img + '?w=64&h=64&fit=crop&auto=format'}
            alt={image.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant='caption' color='text.secondary'>
              Pas d&apos;image
            </Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant='body1' fontWeight='bold' noWrap>
          {image.title}
        </Typography>
        <Typography variant='body2' color='text.secondary' noWrap>
          {image.description}
        </Typography>
        {image.rows === 2 && (
          <Chip label='Image Double' size='small' sx={{ mt: 0.5, backgroundColor: 'primary.main', color: 'primary.contrastText' }} />
        )}
      </Box>
      <Stack direction='row' spacing={0.5}>
        <IconButton
          size='small'
          onClick={function () {
            onEdit(image)
          }}
          sx={{ color: 'text.secondary' }}
        >
          <EditIcon fontSize='small' />
        </IconButton>
        <IconButton
          size='small'
          onClick={function () {
            onDelete(image)
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize='small' />
        </IconButton>
      </Stack>
    </Card>
  )
}

ImageCard.propTypes = {
  image: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    description: PropTypes.string,
    img: PropTypes.string,
    rows: PropTypes.number
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
}
